import { Timestamp } from '../firebase/firebase';
import {
	SafetyCourse,
	SafetyCourseStatus,
	safetyCourseStatus,
	SafetyCourseType,
	SafetyCourseValidationResponse,
} from './SafetyCourse';
import { TimestampToString } from './TypescriptUtilities';

export const getSafetyCourseStatus = (
	safetyCourse?: SafetyCourseValidationResponse | null,
): SafetyCourseStatus => {
	if (safetyCourse?.verified === false) {
		return safetyCourseStatus.Unverified;
	}
	switch (safetyCourse?.type) {
		case SafetyCourseType.ConstructSafe: {
			return safetyCourseStatus.Valid;
		}
		case SafetyCourseType.SiteSafe: {
			return safetyCourse.expiryDate > new Date()
				? safetyCourseStatus.Valid
				: safetyCourseStatus.Expired;
		}
		default: {
			return safetyCourseStatus.NotFound;
		}
	}
};

export const safetyCourseToResponse = (
	safetyCourse: SafetyCourse['course'],
): SafetyCourseValidationResponse => {
	return verifySafetyCourseResponse(
		safetyCourse.id,
		safetyCourse.expiryDate?.toDate() ?? null,
		safetyCourse.name,
		safetyCourse.type,
		safetyCourse.verified,
	);
};

export const responseToSafetyCourse = (
	safetyCourse: SafetyCourseValidationResponse,
): SafetyCourse['course'] => {
	if (safetyCourse.verified === false) {
		return {
			id: safetyCourse.id,
			expiryDate: null,
			name: null,
			type: safetyCourse.type,
			verified: false,
		};
	}
	switch (safetyCourse.type) {
		case SafetyCourseType.ConstructSafe: {
			return {
				id: safetyCourse.id,
				expiryDate: null,
				name: safetyCourse.name,
				type: safetyCourse.type,
				verified: true,
			};
		}
		case SafetyCourseType.SiteSafe: {
			return {
				id: safetyCourse.id,
				expiryDate: Timestamp.fromDate(safetyCourse.expiryDate),
				name: safetyCourse.name,
				type: safetyCourse.type,
				verified: true,
			};
		}
	}
};

export const convertSafetyCourseCFResponse = (
	response: TimestampToString<SafetyCourse>,
): SafetyCourse => {
	const course: SafetyCourse['course'] =
		response.course.verified === true
			? response.course.type === SafetyCourseType.SiteSafe
				? {
						...response.course,
						expiryDate: Timestamp.fromDate(
							new Date(response.course.expiryDate),
						),
				  }
				: {
						...response.course,
						expiryDate: null,
				  }
			: {
					...response.course,
					expiryDate: null,
					name: null,
			  };

	return {
		...response,
		dateCreated: Timestamp.fromDate(new Date(response.dateCreated)),
		course,
	};
};

export const verifySafetyCourseResponse = (
	id: string,
	expiryDate: Date | null,
	name: string | null,
	type: SafetyCourseType,
	verified: boolean,
): SafetyCourseValidationResponse => {
	if (verified === false) {
		return {
			id: id,
			expiryDate: null,
			name: null,
			type: type,
			verified: verified,
		};
	}
	if (name === null) {
		throw Error('verified should be false');
	}
	switch (type) {
		case SafetyCourseType.ConstructSafe: {
			if (expiryDate !== null) {
				throw Error(
					'Construct Safe courses should not have an expiry date',
				);
			}
			return {
				id: id,
				expiryDate: null,
				name: name,
				type: type,
				verified: verified,
			};
		}
		case SafetyCourseType.SiteSafe: {
			if (expiryDate === null) {
				throw Error('Site Safe courses require an expiry date');
			}
			return {
				id: id,
				expiryDate: expiryDate,
				name: name,
				type: type,
				verified: verified,
			};
		}
	}
};
